import { useEffect, useState } from "react";
import { AlertTriangle, Calendar, Layers } from "lucide-react";

import { DropResult } from "@hello-pangea/dnd";

// hooks
import {
  useDeleteData,
  useGetData,
  usePostData,
} from "@/hooks/services/use-query-hooks";
import {
  BaseKanbanBoardType,
  ExtendedJob,
  JobResponse,
  SelectedTechType,
  TechnicianAssigmentPayload,
} from "@/hooks/services/pps/types/job-orders.type";
import { useQueryParams } from "@/hooks/use-query-params";
import {
  AssignJobOrderTechnician,
  JobOrderList,
  UnAssignJobOrderTechnician,
} from "@/hooks/services/pps/handlers/job-orders-api";

import { Badge } from "@/components/ui/badge";

import KanbanBoard from "../components/kanban/board";
import KanbanLoading from "../components/kanban/loading";
import CardFooterContent from "../components/kanban/card-footer";
import KanbanDrawerContent from "../components/kanban/kanban-drawer-content";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";

// utilitize
import {
  initialKanbanTechniciansColumn,
  JobOrderStatus,
  ViewPps,
} from "../utils/constants";

import dayjs from "dayjs";
import { flattenArray } from "@/lib/utils";
import { TechnicianResponse } from "@/hooks/services/pps/types/technicians.type";
import { TechnicianList } from "@/hooks/services/pps/handlers/technicians-api";
import { taskMapperItem, getVariantByStatus } from "../utils/utils";
import { createFileRoute } from "@tanstack/react-router";
import {
  parseFiltersFromQuery,
  parseSortsFromQuery,
  type DynamicFilter,
  type DynamicSort,
} from "@/components/search-filter-bar";

export const Route = createFileRoute("/(planner-screens)/planner/man-power")({
  component: RouteComponent,
});

function RouteComponent() {
  const { params } = useQueryParams();

  const assignMutation = usePostData<TechnicianAssigmentPayload, any>(
    AssignJobOrderTechnician(),
  );
  const unAssignMutation = useDeleteData<TechnicianAssigmentPayload, any>(
    UnAssignJobOrderTechnician(),
  );

  // Read filters and sorts from URL search params (managed by parent planner.tsx)
  const dynamicFilters: DynamicFilter[] = params.filters
    ? parseFiltersFromQuery(params.filters as string)
    : [];

  const dynamicSorts: DynamicSort[] = params.sorts
    ? parseSortsFromQuery(params.sorts as string)
    : [];

  const { data, isLoading, refetch, isRefetching } = useGetData<JobResponse>(
    JobOrderList({
      ...params,
      viewId: ViewPps.planner.id,
      page: 1,
      pageSize: 100,
      ...(dynamicSorts.length > 0 && {
        encodeQuery: { sorts: params.sorts },
      }),
      ...(dynamicFilters.length > 0 && {
        encodeQuery: { filters: params.filters },
      }),
    }),
  );

  const { data: technicianData, isLoading: isLoadingTechnicians } =
    useGetData<TechnicianResponse>(TechnicianList({}));

  // use two load state from state and hooks to prevent hydration for this case
  const [loading, setLoading] = useState<boolean>(false);
  const [columns, setColumns] = useState<BaseKanbanBoardType<ExtendedJob>[]>(
    initialKanbanTechniciansColumn || [],
  );
  const [selectedCard, setSelectedCard] = useState<SelectedTechType>({
    task: {},
    show: false,
  });

  useEffect(() => {
    setLoading(true);
    if (
      data &&
      data.data &&
      technicianData &&
      !isLoading &&
      !isLoadingTechnicians
    ) {
      const uniqueTechnicians: any[] = [];
      const technicians = data?.data?.map((i: any) => i.ppsTechnicians);

      flattenArray(technicians as any).forEach((i: any) => {
        if (!uniqueTechnicians.some((tech) => tech.id === i.id)) {
          uniqueTechnicians.push(i);
        }
      });

      const getTechnicianColumns = () => {
        // if (uniqueTechnicians.length > 0) {
        //   return uniqueTechnicians.map((i) => ({ id: `${i.id}`, title: i.name, tasks: [] }));
        // }
        return technicianData?.data.map((i) => ({
          id: `${i.id}`,
          title: i.name,
          tasks: [],
        }));
      };
      const initialColumns: BaseKanbanBoardType<ExtendedJob>[] = [
        ...initialKanbanTechniciansColumn,
        ...getTechnicianColumns(),
      ];

      data?.data.forEach((item: any, index: number) => {
        if (item.ppsTechnicians.length > 0) {
          item.ppsTechnicians.forEach((technician: any) => {
            const technicianEntry = initialColumns.find(
              (i) => i.id === `${technician.id}`,
            );
            const formattedTechName = `${technician.name} ${technician.id}`
              .toLowerCase()
              .replace(/\s/g, "-");
            if (technicianEntry) {
              technicianEntry.tasks.push(
                taskMapperItem(item, formattedTechName),
              );
            }
          });
        } else {
          const unassignedColumn = initialColumns.find(
            (i) => i.id === "not-assigned",
          );
          if (unassignedColumn) {
            unassignedColumn.tasks.push(taskMapperItem(item, "", index));
          }
        }

        setColumns(initialColumns);
        setLoading(false);
      });
    }
  }, [data, technicianData]);

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (
      !destination ||
      (source.droppableId === destination.droppableId &&
        source.index === destination.index)
    ) {
      return;
    }
    const sourceColumn = columns.find((col) => col.id === source.droppableId);
    const destinationColumn = columns.find(
      (col) => col.id === destination.droppableId,
    );
    if (!sourceColumn || !destinationColumn) {
      return;
    }
    const sourceTasks = sourceColumn.tasks;
    const destinationTasks = destinationColumn.tasks;
    // find index and delete count the task from the source
    const [movedTask] = sourceTasks.splice(source.index, 1);
    // find index with zero deleteion and add the item
    destinationTasks.splice(destination.index, 0, movedTask);
    setColumns((prevColumns) =>
      prevColumns.map((column) => {
        if (column.id === sourceColumn.id) {
          return { ...column, tasks: sourceTasks };
        }
        if (column.id === destinationColumn.id) {
          return { ...column, tasks: destinationTasks };
        }
        return column;
      }),
    );

    if (sourceColumn.id === "not-assigned") {
      assignMutation.mutate({
        jobOrderId: movedTask.jobOrderId,
        technicianIds: [Number(destination.droppableId)],
      });
    } else if (sourceColumn.id !== "not-assigned") {
      unAssignMutation.mutate({
        jobOrderId: movedTask.jobOrderId,
        technicianIds: [Number(sourceColumn.id)],
      });

      assignMutation.mutate({
        jobOrderId: movedTask.jobOrderId,
        technicianIds: [Number(destination.droppableId)],
      });
    }
  };

  const CardComponent = ({ task }: { task?: any }): JSX.Element => {
    if (!task) {
      return <div>No task available</div>;
    }
    // const toolsCount = task.tools.length;
    // const consumablesCount = task.consumables.length;

    return (
      <Card
        onClick={() => setSelectedCard((prev) => ({ task, show: !prev.show }))}
        className="aero:mb-2 aero:gap-0 aero:p-0 aero:bg-white aero:dark:bg-background aero:shadow-sm aero:hover:shadow-md aero:transition-shadow aero:duration-200"
      >
        <CardHeader className="aero:p-4 aero:pb-0">
          <div className="aero:flex aero:items-start aero:justify-between">
            <div className="aero:flex aero:items-center aero:space-x-2">
              <Layers className="aero:h-5 aero:w-5 aero:!text-blue-500" />
              <span className="aero:!text-sm aero:!font-medium aero:!text-gray-500">
                {task?.part?.manufacturer}
              </span>
            </div>
            <div className="aero:flex aero:items-center aero:gap-2 aero:!text-muted-foreground">
              <span className="aero:!text-sm">
                {dayjs(task.dueDate).format("DD MMM YYYY")}
              </span>
              <Calendar className="aero:h-4 aero:w-4" />
            </div>
            {/* TEMPORARY */}
            {/* <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant='ghost'
                  size='icon'
                  className='h-8 w-8'
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className='h-4 w-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end' className='w-48'>
                <DropdownMenuLabel>Card Actions</DropdownMenuLabel>
                <DropdownMenuGroup>
                  <DropdownMenuItem onClick={(e) => e.stopPropagation()}>
                    <Copy className='mr-2 h-4 w-4' />
                    Duplicate Card
                  </DropdownMenuItem>
                </DropdownMenuGroup>

                <DropdownMenuSeparator />
                <DropdownMenuItem className='text-destructive' onClick={(e) => e.stopPropagation()}>
                  <Trash className='mr-2 h-4 w-4' />
                  Delete Card
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu> */}
          </div>
        </CardHeader>
        <CardContent className="aero:p-4">
          <h2 className="aero:!text-lg aero:!font-semibold aero:mb-2">
            {task.jobdOrderNo}
          </h2>
          <p className="aero:!text-sm aero:!text-gray-600 aero:mb-4">
            {task?.part?.description}
          </p>

          <div className="aero:flex aero:flex-col aero:gap-2">
            <div className="aero:flex aero:gap-2 aero:items-center">
              <Badge
                variant="outline"
                className="aero:w-fit aero:!text-gray-700 aero:bg-gray-50 aero:dark:bg-background aero:dark:text-gray-400 aero:hover:bg-gray-50"
              >
                Part No: {task?.part?.partNumber}
              </Badge>
              <span className="aero:!text-xs">
                {task?.standardHours?.slice(0, 2) || "00"} <span>hrs</span>
              </span>
            </div>
            <Badge className="aero:w-fit aero:bg-blue-100 aero:dark:bg-secondary aero:!text aero:!text-blue-700 aero:hover:bg-blue-100 aero:border-0">
              Serial Number: {task?.part?.serialNumber}
            </Badge>

            <div className="aero:mt-3">
              <Badge variant={getVariantByStatus(task.status?.statusName)}>
                {task?.status?.statusName || JobOrderStatus.NotStarted}
              </Badge>
            </div>
          </div>
        </CardContent>

        {/* Footer */}
        <CardFooter className="aero:px-4 aero:py-3 aero:bg-gray-50 aero:dark:bg-background aero:flex aero:items-center aero:justify-between">
          <div className="aero:flex aero:items-center aero:justify-center aero:gap-1">
            {/* <CardFooterContent icon={Wrench} value={toolsCount} />
            <CardFooterContent icon={Droplets} value={consumablesCount} /> */}
            <CardFooterContent icon={AlertTriangle} value={task.priority} />
          </div>
        </CardFooter>
      </Card>
    );
  };

  return (
    <div className="aero:flex aero:flex-col aero:gap-4">
      {/* Subtitle */}
      <div className="aero:flex aero:items-center aero:gap-2">
        <span className="aero:w-[3px] aero:h-7 aero:bg-brand-primary-600 aero:rounded-full" />
        <h2 className="aero:font-bold aero:text-lg aero:leading-7 aero:text-brand-primary-600">
          Man Power Planning
        </h2>
      </div>

      <div className="aero:overflow-auto">
        {loading || isRefetching ? (
          <KanbanLoading />
        ) : (
          <KanbanBoard
            columns={columns}
            onDragEnd={onDragEnd}
            setColumns={setColumns}
          >
            <CardComponent />
          </KanbanBoard>
        )}
      </div>

      <KanbanDrawerContent
        open={selectedCard.show}
        onOpenChange={(open) =>
          setSelectedCard((prev) => ({
            ...prev,
            show: open,
          }))
        }
        withoutTechnician
        data={selectedCard.task}
        refetch={refetch}
      />
    </div>
  );
}
